// manufacturing/RawMaterialReceive.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
//import Swal from "sweetalert2";
//import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import {
    Plus, Search, RotateCcw, Eye, Printer, ChevronUp,
    Download, Calendar, MapPin, Package, CheckCircle, XCircle, AlertCircle,
    Truck, User, Info, Clipboard
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";
import ImageWithBasePath from '../../core/img/imagewithbasebath';

//const MySwal = withReactContent(Swal);

const RawMaterialReceive = () => {
    const dispatchRedux = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State management
    const [receives, setReceives] = useState([]);
    const [dispatches, setDispatches] = useState([]);
    const [selectedDispatch, setSelectedDispatch] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingDispatches, setLoadingDispatches] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    
    
    // Modal states
    const [showReceiveModal, setShowReceiveModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedReceive, setSelectedReceive] = useState(null);
    
    // Manufacturing Unit Info
    const [manufacturingUnit, setManufacturingUnit] = useState({
        name: 'Manufacturing Unit 1',
        location: 'Industrial Area, Chennai',
        incharge: 'Production Manager',
        currentStock: 0
    });
    
    // Form states
    const [formData, setFormData] = useState({
        dispatch_id: '',
        receive_date: new Date().toISOString().split('T')[0],
        received_by: '',
        notes: '',
        items: []
    });
    
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        from_date: '',
        to_date: ''
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Check permissions
    const canCreate = manufacturingActions.hasManufacturingPermission('create-receive');
    const canView = manufacturingActions.hasManufacturingPermission('view-receive');

    // Load manufacturing unit info
    const loadManufacturingUnitInfo = useCallback(async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            setManufacturingUnit(prev => ({
                ...prev,
                name: user.manufacturing_unit || 'Manufacturing Unit 1',
                incharge: user.name || 'Production Manager'
            }));
        } catch (error) {
            console.error('Error loading manufacturing unit info:', error);
        }
    }, []);

    // Load receives
    const loadReceives = useCallback(async (page = pagination.page) => {
        if (!canView) {
            toast.error('You do not have permission to view receives');
            return;
        }

        try {
            setLoading(true);
            
            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                status: filters.status,
                from_date: filters.from_date,
                to_date: filters.to_date
            };

            const result = await manufacturingActions.getReceives(params);

            if (result.success) {
                setReceives(result.data || []);
                setPagination(prev => ({
                    ...prev,
                    page: result.page || page,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                }));
            } else {
                toast.error(result.message || 'Failed to load receives');
            }
        } catch (error) {
            console.error('Error loading receives:', error);
            toast.error('Failed to load receives');
        } finally {
            setLoading(false);
        }
    }, [canView, filters, pagination.limit, pagination.page]);

    // Load dispatches that are ready for receiving (dispatched or partially_received)
    const loadPendingDispatches = useCallback(async () => {
        try {
            setLoadingDispatches(true);
            // Fetch dispatches with status 'dispatched' or 'partially_received'
            const result = await manufacturingActions.getDispatches({ 
                status: 'dispatched',
                limit: 100 
            });
            
            // Also fetch partially received dispatches
            const resultPartial = await manufacturingActions.getDispatches({ 
                status: 'partially_received',
                limit: 100 
            });
            
            let allDispatches = [];
            if (result.success && result.data) {
                allDispatches = [...allDispatches, ...result.data];
            }
            if (resultPartial.success && resultPartial.data) {
                allDispatches = [...allDispatches, ...resultPartial.data];
            }
            
            setDispatches(allDispatches);
            
            if (allDispatches.length === 0) {
                toast.info('No dispatches available for receiving. Please ensure dispatches are marked as "Dispatched" first.');
            }
        } catch (error) {
            console.error('Error loading dispatches:', error);
            toast.error('Failed to load dispatches for receiving');
        } finally {
            setLoadingDispatches(false);
        }
    }, []);

    // Load data on mount
    useEffect(() => {
        loadReceives(1);
        loadPendingDispatches();
        loadManufacturingUnitInfo();
    }, [loadReceives, loadPendingDispatches, loadManufacturingUnitInfo]);

    // Handle dispatch selection
    const handleDispatchSelect = async (selectedOption) => {
        if (selectedOption && selectedOption.value) {
            setSubmitting(true);
            try {
                const result = await manufacturingActions.getDispatch(selectedOption.value);
                if (result.success && result.data) {
                    const dispatch = result.data;
                    setSelectedDispatch(dispatch);
                    
                    const itemsArray = Array.isArray(dispatch.items) ? dispatch.items : [];
                    
                    setFormData(prev => ({
                        ...prev,
                        dispatch_id: dispatch.id,
                        items: itemsArray.map(item => ({
                            dispatch_item_id: item.dispatch_item_id || item.id,
                            product_id: item.product_id,
                            product_name: item.product_name || 'Unknown Product',
                            sku: item.sku || '',
                            dispatched_quantity: parseFloat(item.dispatched_quantity || item.quantity || 0),
                            received_quantity: 0,
                            damage_quantity: 0,
                            damage_reason: '',
                            unit: item.unit || 'PCS',
                            remarks: item.remarks || ''
                        }))
                    }));
                    
                    toast.info(`Dispatch ${dispatch.dispatch_no} selected. Please enter received quantities.`);
                } else {
                    toast.error('Failed to load dispatch details');
                }
            } catch (error) {
                console.error('Error loading dispatch:', error);
                toast.error('Failed to load dispatch details');
            } finally {
                setSubmitting(false);
            }
        } else {
            setSelectedDispatch(null);
            setFormData(prev => ({
                ...prev,
                dispatch_id: '',
                items: []
            }));
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle received quantity change
    const handleReceivedQuantityChange = (index, quantity) => {
        const updatedItems = [...formData.items];
        const qty = parseFloat(quantity) || 0;
        const dispatchedQty = updatedItems[index].dispatched_quantity || 0;
        
        if (qty > dispatchedQty) {
            toast.warning(`Cannot receive more than dispatched quantity (${dispatchedQty})`);
            updatedItems[index].received_quantity = dispatchedQty;
        } else {
            updatedItems[index].received_quantity = qty;
        }
        
        setFormData(prev => ({ ...prev, items: updatedItems }));
        
        if (errors[`received_${index}`]) {
            setErrors(prev => ({ ...prev, [`received_${index}`]: '' }));
        }
    };

    // Handle damage quantity change
    const handleDamageQuantityChange = (index, quantity) => {
        const updatedItems = [...formData.items];
        const qty = parseFloat(quantity) || 0;
        const receivedQty = updatedItems[index].received_quantity || 0;
        
        if (qty > receivedQty) {
            toast.warning(`Damage quantity cannot exceed received quantity (${receivedQty})`);
            updatedItems[index].damage_quantity = receivedQty;
        } else {
            updatedItems[index].damage_quantity = qty;
        }
        
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    // Handle damage reason change
    const handleDamageReasonChange = (index, reason) => {
        const updatedItems = [...formData.items];
        updatedItems[index].damage_reason = reason;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.dispatch_id) {
            newErrors.dispatch_id = 'Please select a dispatch';
        }
        
        let hasItems = false;
        for (let i = 0; i < formData.items.length; i++) {
            const item = formData.items[i];
            if (item.received_quantity > 0 || item.damage_quantity > 0) {
                hasItems = true;
            }
            if (item.received_quantity < 0) {
                newErrors[`received_${i}`] = 'Invalid quantity';
            }
            if (item.damage_quantity < 0) {
                newErrors[`damage_${i}`] = 'Invalid quantity';
            }
            if (item.damage_quantity > 0 && !item.damage_reason) {
                newErrors[`damage_reason_${i}`] = 'Please provide reason for damage';
            }
        }
        
        if (!hasItems) {
            newErrors.items = 'At least one item must be received';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Calculate totals
    const calculateTotals = () => {
        const totalReceived = formData.items.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
        const totalDamage = formData.items.reduce((sum, item) => sum + (item.damage_quantity || 0), 0);
        const totalDispatched = formData.items.reduce((sum, item) => sum + (item.dispatched_quantity || 0), 0);
        const pendingReceive = totalDispatched - totalReceived - totalDamage;
        const receivedPercentage = totalDispatched > 0 ? (totalReceived / totalDispatched) * 100 : 0;
        
        return { totalReceived, totalDamage, totalDispatched, pendingReceive, receivedPercentage };
    };

    // Handle form submission
const handleSubmit = async () => {
    if (!validateForm()) {
        toast.error('Please fix the errors in the form');
        return;
    }

    setSubmitting(true);
    
    // Filter items that have received or damage quantity
    const itemsToSend = formData.items
        .filter(item => (item.received_quantity > 0 || item.damage_quantity > 0))
        .map(item => ({
            dispatch_item_id: item.dispatch_item_id,
            product_id: item.product_id,
            received_quantity: parseFloat(item.received_quantity) || 0,
            damage_quantity: parseFloat(item.damage_quantity) || 0,
            damage_reason: item.damage_reason || null,
            remarks: item.remarks || null
        }));
    
    if (itemsToSend.length === 0) {
        toast.error('No items to receive');
        setSubmitting(false);
        return;
    }
    
    const dataToSend = {
        dispatch_id: formData.dispatch_id,
        receive_date: formData.receive_date,
        received_by: formData.received_by || null,
        notes: formData.notes || null,
        items: itemsToSend
    };
    
    console.log('Sending data:', dataToSend); // Debug log
    
    try {
        const result = await manufacturingActions.createReceive(dataToSend);
        
        if (result && result.success) {
            toast.success(result.message || 'Materials received successfully');
            resetForm();
            setShowReceiveModal(false);
            loadReceives(1);
            loadPendingDispatches();
        } else {
            toast.error(result?.message || 'Failed to receive materials');
        }
    } catch (error) {
        console.error('Error receiving materials:', error);
        const errorMsg = error.response?.data?.message || error.message || 'Failed to receive materials';
        toast.error(errorMsg);
    } finally {
        setSubmitting(false);
    }
};

    // Reset form
    const resetForm = () => {
        setFormData({
            dispatch_id: '',
            receive_date: new Date().toISOString().split('T')[0],
            received_by: '',
            notes: '',
            items: []
        });
        setSelectedDispatch(null);
        setErrors({});
    };

    // View receive details
    const handleViewReceive = async (receive) => {
        try {
            const result = await manufacturingActions.getReceive(receive.id);
            if (result.success) {
                setSelectedReceive(result.data);
                setShowViewModal(true);
            } else {
                toast.error(result.message || 'Failed to load receive details');
            }
        } catch (error) {
            console.error('Error loading receive details:', error);
            toast.error('Failed to load receive details');
        }
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format datetime
    const formatDateTime = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const badgeClass = status === 'completed' ? 'bg-success' : 
                          status === 'pending' ? 'bg-warning' : 
                          status === 'partial' ? 'bg-info' : 'bg-danger';
        const icon = status === 'completed' ? <CheckCircle size={12} className="me-1" /> :
                     status === 'pending' ? <AlertCircle size={12} className="me-1" /> :
                     status === 'partial' ? <Info size={12} className="me-1" /> :
                     <XCircle size={12} className="me-1" />;
        return (
            <span className={`badge ${badgeClass} d-inline-flex align-items-center`}>
                {icon}
                {status?.toUpperCase() || 'N/A'}
            </span>
        );
    };

    // Dispatch options for dropdown - Only show dispatches that are dispatched or partially received
    const dispatchOptions = Array.isArray(dispatches) ? dispatches
        .filter(dispatch => dispatch.status === 'dispatched' || dispatch.status === 'partially_received')
        .map(dispatch => ({
            value: dispatch.id,
            label: `${dispatch.dispatch_no} - From: ${dispatch.from_location} (${formatDate(dispatch.dispatch_date)})`,
            dispatch: dispatch
        })) : [];

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    const totals = calculateTotals();

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Raw Material Receive - {manufacturingUnit.name}</h4>
                        <h6>Receive and inspect raw materials from dispatches at manufacturing unit</h6>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'PDF')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="PDF" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Excel')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="Excel" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <Link onClick={() => window.print()}>
                                    <Printer />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={() => { loadReceives(1); loadPendingDispatches(); }}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatchRedux(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>

                    {canCreate && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={() => setShowReceiveModal(true)}>
                                <Plus size={18} className="me-2" />
                                Receive Materials
                            </button>
                        </div>
                    )}
                </div>

                {/* Manufacturing Unit Info Cards */}
                <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card bg-primary bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary p-3 rounded me-3">
                                        <Package size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Manufacturing Unit</p>
                                        <h5 className="mb-0">{manufacturingUnit.name}</h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card bg-info bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-info p-3 rounded me-3">
                                        <MapPin size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Location</p>
                                        <h6 className="mb-0">{manufacturingUnit.location}</h6>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card bg-success bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-success p-3 rounded me-3">
                                        <User size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Incharge</p>
                                        <h6 className="mb-0">{manufacturingUnit.incharge}</h6>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card bg-warning bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-warning p-3 rounded me-3">
                                        <Package size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Receives</p>
                                        <h3 className="mb-0">{pagination.total}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-4">
                                <div className="mb-3">
                                    <label>Search</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Receive no, dispatch no..."
                                            value={filters.search}
                                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                            onKeyPress={(e) => e.key === 'Enter' && loadReceives(1)}
                                        />
                                        <button className="btn btn-primary" onClick={() => loadReceives(1)}>
                                            <Search size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>Status</label>
                                    <Select
                                        options={statusOptions}
                                        isClearable
                                        placeholder="All Status"
                                        onChange={(selected) => {
                                            setFilters(prev => ({ ...prev, status: selected?.value || '' }));
                                            loadReceives(1);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.from_date}
                                        onChange={(e) => {
                                            setFilters(prev => ({ ...prev, from_date: e.target.value }));
                                            loadReceives(1);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.to_date}
                                        onChange={(e) => {
                                            setFilters(prev => ({ ...prev, to_date: e.target.value }));
                                            loadReceives(1);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-1">
                                <div className="mb-3">
                                    <label>&nbsp;</label>
                                    <button className="btn btn-secondary w-100" onClick={() => {
                                        setFilters({ search: '', status: '', from_date: '', to_date: '' });
                                        loadReceives(1);
                                    }}>
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Receives Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : !Array.isArray(receives) || receives.length === 0 ? (
                            <div className="text-center py-5">
                                <Download size={48} className="text-muted mb-3" />
                                <h5>No receives found at {manufacturingUnit.name}</h5>
                                <p className="text-muted">Receive materials from dispatches to get started</p>
                                {canCreate && (
                                    <button className="btn btn-primary" onClick={() => setShowReceiveModal(true)}>
                                        <Plus size={16} className="me-2" />
                                        Receive Materials
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th width="40px">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedRows.length === receives.length && receives.length > 0}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedRows(receives.map(r => r.id));
                                                        } else {
                                                            setSelectedRows([]);
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th>Receive No</th>
                                            <th>Date</th>
                                            <th>Dispatch No</th>
                                            <th>From Location</th>
                                            <th>Items</th>
                                            <th>Received Qty</th>
                                            <th>Damage Qty</th>
                                            <th>Status</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {receives.map((receive) => (
                                            <tr key={receive.id}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedRows.includes(receive.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedRows([...selectedRows, receive.id]);
                                                            } else {
                                                                setSelectedRows(selectedRows.filter(id => id !== receive.id));
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td><strong>{receive.receive_no}</strong></td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Calendar size={14} className="me-1 text-muted" />
                                                        {formatDate(receive.receive_date)}
                                                    </div>
                                                </td>
                                                <td>{receive.dispatch_no}</td>
                                                <td>
                                                    <MapPin size={14} className="me-1 text-muted" />
                                                    {receive.from_location}
                                                </td>
                                                <td>{receive.total_items || 0}</td>
                                                <td>{receive.total_received || 0}</td>
                                                <td>
                                                    {receive.total_damage > 0 ? (
                                                        <span className="text-danger">
                                                            <XCircle size={14} className="me-1" />
                                                            {receive.total_damage}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">0</span>
                                                    )}
                                                </td>
                                                <td>{getStatusBadge(receive.status)}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-info"
                                                        onClick={() => handleViewReceive(receive)}
                                                        title="View Details"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                </div>
                                <nav>
                                    <ul className="pagination">
                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadReceives(pagination.page - 1)}>
                                                Previous
                                            </button>
                                        </li>
                                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                            let pageNum = pagination.page;
                                            if (pagination.totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (pagination.page <= 3) {
                                                pageNum = i + 1;
                                            } else if (pagination.page >= pagination.totalPages - 2) {
                                                pageNum = pagination.totalPages - 4 + i;
                                            } else {
                                                pageNum = pagination.page - 2 + i;
                                            }
                                            return (
                                                <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => loadReceives(pageNum)}>
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadReceives(pagination.page + 1)}>
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Receive Materials Modal */}
            {showReceiveModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <Download size={18} className="me-2" />
                                    Receive Materials at {manufacturingUnit.name}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowReceiveModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {/* Manufacturing Unit Header */}
                                <div className="alert alert-info mb-4">
                                    <div className="d-flex align-items-center">
                                        <Package size={24} className="me-3" />
                                        <div>
                                            <strong>Receiving at: {manufacturingUnit.name}</strong>
                                            <br />
                                            <small>Location: {manufacturingUnit.location} | Incharge: {manufacturingUnit.incharge}</small>
                                        </div>
                                    </div>
                                </div>

                                {/* Step 1: Select Dispatch Section */}
                                <div className="row mb-4">
                                    <div className="col-md-12">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">
                                                    <Truck size={16} className="me-2 text-primary" />
                                                    Step 1: Select Dispatch to Receive
                                                </h6>
                                                {loadingDispatches ? (
                                                    <div className="text-center py-3">
                                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                        Loading dispatches...
                                                    </div>
                                                ) : (
                                                    <>
                                                        <Select
                                                            options={dispatchOptions}
                                                            onChange={handleDispatchSelect}
                                                            placeholder={dispatchOptions.length === 0 ? "No dispatches available for receiving. Please mark dispatches as 'Dispatched' first." : "Choose dispatch to receive at manufacturing unit"}
                                                            isDisabled={submitting || loadingDispatches}
                                                            className={errors.dispatch_id ? 'is-invalid' : ''}
                                                            noOptionsMessage={() => "No dispatches available. Please ensure dispatches are marked as 'Dispatched'."}
                                                        />
                                                        {errors.dispatch_id && <div className="text-danger small mt-1">{errors.dispatch_id}</div>}
                                                        <small className="text-muted mt-2 d-block">
                                                            <Info size={12} className="me-1" />
                                                            Select a dispatch that has been sent from Head Office to this manufacturing unit and has status Dispatched or Partially Received
                                                        </small>
                                                        {dispatchOptions.length === 0 && !loadingDispatches && (
                                                            <div className="alert alert-warning mt-3 mb-0">
                                                                <AlertCircle size={16} className="me-2" />
                                                                <strong>No dispatches available for receiving.</strong> Please go to Raw Material Dispatch and create/update dispatches to status Dispatched.
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Step 2: Receive Information Section */}
                                <div className="row mb-4">
                                    <div className="col-md-6">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">
                                                    <Info size={16} className="me-2 text-primary" />
                                                    Step 2: Receive Information
                                                </h6>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <label className="form-label">Receive Date *</label>
                                                        <input
                                                            type="date"
                                                            name="receive_date"
                                                            className="form-control"
                                                            value={formData.receive_date}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Received By</label>
                                                        <input
                                                            type="text"
                                                            name="received_by"
                                                            className="form-control"
                                                            value={formData.received_by}
                                                            onChange={handleInputChange}
                                                            placeholder="Person receiving materials"
                                                        />
                                                        <small className="text-muted">Person who physically received the materials</small>
                                                    </div>
                                                    <div className="col-12 mt-3">
                                                        <label className="form-label">Inspection Notes</label>
                                                        <textarea
                                                            name="notes"
                                                            className="form-control"
                                                            value={formData.notes}
                                                            onChange={handleInputChange}
                                                            rows="2"
                                                            placeholder="Quality inspection notes, packaging condition, etc."
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Dispatch Information Card */}
                                    {selectedDispatch && (
                                        <div className="col-md-6">
                                            <div className="card bg-info bg-opacity-10">
                                                <div className="card-body alert-info">
                                                    <h6 className="mb-3">
                                                        <Clipboard size={16} className="me-2 text-info" />
                                                        Dispatch Information
                                                    </h6>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <p className="mb-1"><strong>Dispatch No:</strong> {selectedDispatch.dispatch_no}</p>
                                                            <p className="mb-1"><strong>Dispatch Date:</strong> {formatDate(selectedDispatch.dispatch_date)}</p>
                                                            <p className="mb-1"><strong>From Location:</strong> {selectedDispatch.from_location}</p>
                                                            <p className="mb-1"><strong>Status:</strong> {getStatusBadge(selectedDispatch.status)}</p>
                                                        </div>
                                                        <div className="col-md-6">
                                                            {selectedDispatch.reference_no && (
                                                                <p className="mb-1"><strong>Reference No:</strong> {selectedDispatch.reference_no}</p>
                                                            )}
                                                            <p className="mb-1"><strong>Total Items:</strong> {selectedDispatch.total_items}</p>
                                                            <p className="mb-1"><strong>Total Quantity:</strong> {selectedDispatch.total_quantity} units</p>
                                                        </div>
                                                        {selectedDispatch.notes && (
                                                            <div className="col-12 mt-2">
                                                                <p className="mb-0"><strong>Dispatch Notes:</strong> {selectedDispatch.notes}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Step 3: Items Section */}
                                {selectedDispatch ? (
                                    <div className="border-top pt-3">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="fw-bold mb-0">
                                                <Package size={16} className="me-2 text-success" />
                                                Step 3: Enter Received Quantities at Manufacturing Unit
                                            </h6>
                                            <div className="d-flex gap-3">
                                                <div className="small text-muted">
                                                    <strong>Total Dispatched:</strong> {totals.totalDispatched} units
                                                </div>
                                                <div className="small text-success">
                                                    <strong>Received:</strong> {totals.totalReceived} units ({totals.receivedPercentage.toFixed(1)}%)
                                                </div>
                                                <div className="small text-danger">
                                                    <strong>Damage:</strong> {totals.totalDamage} units
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {errors.items && <div className="alert alert-danger">{errors.items}</div>}
                                        
                                        {/* Progress Bar */}
                                        {totals.totalDispatched > 0 && (
                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between small mb-1">
                                                    <span>Receive Progress at Manufacturing Unit</span>
                                                    <span>{totals.receivedPercentage.toFixed(1)}%</span>
                                                </div>
                                                <div className="progress" style={{ height: '8px' }}>
                                                    <div 
                                                        className="progress-bar bg-success" 
                                                        style={{ width: `${totals.receivedPercentage}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: '25%' }}>Product / Raw Material</th>
                                                        <th style={{ width: '10%' }}>SKU Code</th>
                                                        <th style={{ width: '12%' }}>Dispatched Qty</th>
                                                        <th style={{ width: '15%' }}>Received Qty at Factory *</th>
                                                        <th style={{ width: '13%' }}>Damage Qty</th>
                                                        <th style={{ width: '20%' }}>Reason for Damage / Rejection</th>
                                                        <th style={{ width: '5%' }}>Unit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.items.map((item, index) => (
                                                        <tr key={item.dispatch_item_id || index}>
                                                            <td>
                                                                <strong>{item.product_name}</strong>
                                                                {item.remarks && (
                                                                    <div className="small text-muted mt-1">{item.remarks}</div>
                                                                )}
                                                            </td>
                                                            <td><code>{item.sku}</code></td>
                                                            <td className="fw-bold text-primary">{item.dispatched_quantity}</td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control ${errors[`received_${index}`] ? 'is-invalid' : ''}`}
                                                                    value={item.received_quantity || ''}
                                                                    onChange={(e) => handleReceivedQuantityChange(index, e.target.value)}
                                                                    step="1"
                                                                    min="0"
                                                                    max={item.dispatched_quantity}
                                                                    placeholder="Enter received qty"
                                                                    style={{ width: '120px' }}
                                                                />
                                                                {errors[`received_${index}`] && (
                                                                    <div className="text-danger small mt-1">{errors[`received_${index}`]}</div>
                                                                )}
                                                                <small className="text-muted d-block">Enter quantity physically received</small>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control ${errors[`damage_${index}`] ? 'is-invalid' : ''}`}
                                                                    value={item.damage_quantity || ''}
                                                                    onChange={(e) => handleDamageQuantityChange(index, e.target.value)}
                                                                    step="1"
                                                                    min="0"
                                                                    placeholder="Damage"
                                                                    disabled={item.received_quantity === 0}
                                                                    style={{ width: '100px' }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className={`form-control ${errors[`damage_reason_${index}`] ? 'is-invalid' : ''}`}
                                                                    value={item.damage_reason || ''}
                                                                    onChange={(e) => handleDamageReasonChange(index, e.target.value)}
                                                                    placeholder="Reason for damage (e.g., transit damage, poor quality)"
                                                                    disabled={item.damage_quantity === 0}
                                                                />
                                                                {errors[`damage_reason_${index}`] && (
                                                                    <div className="text-danger small mt-1">{errors[`damage_reason_${index}`]}</div>
                                                                )}
                                                            </td>
                                                            <td>{item.unit}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="2" className="text-end fw-bold">Totals:</td>
                                                        <td className="fw-bold text-primary">{totals.totalDispatched}</td>
                                                        <td className="fw-bold text-success">{totals.totalReceived}</td>
                                                        <td className="fw-bold text-danger">{totals.totalDamage}</td>
                                                        <td className="fw-bold text-warning">{totals.pendingReceive}</td>
                                                        <td></td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan="7" className="text-end text-muted small">
                                                            <AlertCircle size={12} className="me-1" />
                                                            Note: Received Qty + Damage Qty cannot exceed Dispatched Qty. Damaged materials will be recorded as waste.
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                        
                                        {/* Summary Alerts */}
                                        {totals.pendingReceive > 0 && totals.pendingReceive === totals.totalDispatched && (
                                            <div className="alert alert-warning mt-3">
                                                <AlertCircle size={16} className="me-2" />
                                                <strong>No Items Received at Manufacturing Unit:</strong> You have not received any items from this dispatch. Please enter received quantities or cancel this receive.
                                            </div>
                                        )}
                                        {totals.pendingReceive > 0 && totals.pendingReceive !== totals.totalDispatched && (
                                            <div className="alert alert-warning mt-3">
                                                <AlertCircle size={16} className="me-2" />
                                                <strong>Partial Receive at Manufacturing Unit:</strong> {totals.pendingReceive} units are pending. This dispatch will be marked as Partially Received. Please check if remaining materials are in transit.
                                            </div>
                                        )}
                                        {totals.totalDamage > 0 && (
                                            <div className="alert alert-danger mt-3">
                                                <XCircle size={16} className="me-2" />
                                                <strong>Damage Reported at Manufacturing Unit:</strong> {totals.totalDamage} units are damaged. These will be recorded as waste. Please ensure reasons are provided for quality control.
                                            </div>
                                        )}
                                        {totals.totalReceived === totals.totalDispatched && totals.totalDispatched > 0 && totals.totalDamage === 0 && (
                                            <div className="alert alert-success mt-3">
                                                <CheckCircle size={16} className="me-2" />
                                                <strong>Complete Receive at Manufacturing Unit:</strong> All items have been received successfully with no damage. Materials are ready for production.
                                            </div>
                                        )}
                                        {totals.totalReceived === totals.totalDispatched && totals.totalDispatched > 0 && totals.totalDamage > 0 && (
                                            <div className="alert alert-success mt-3">
                                                <CheckCircle size={16} className="me-2" />
                                                <strong>Complete Receive with Damage:</strong> All items have been received, but {totals.totalDamage} units are damaged. These will be moved to waste stock.
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="border-top pt-3 text-center py-5">
                                        <Package size={48} className="text-muted mb-3" />
                                        <p className="text-muted">Select a dispatch to view and receive items at {manufacturingUnit.name}</p>
                                        <p className="text-muted small">Once selected, all dispatched items will appear here for you to enter received quantities</p>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowReceiveModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleSubmit}
                                    disabled={submitting || !selectedDispatch}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle size={16} className="me-2" />
                                            Confirm Receive at Manufacturing Unit
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Receive Modal */}
            {showViewModal && selectedReceive && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <Download size={18} className="me-2" />
                                    Receive Details - {selectedReceive.receive_no}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-info mb-4">
                                    <div className="d-flex align-items-center">
                                        <Package size={20} className="me-2" />
                                        <span>Materials received at <strong>{manufacturingUnit.name}</strong></span>
                                    </div>
                                </div>
                                
                                <div className="row mb-4">
                                    <div className="col-md-6">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">Receive Information</h6>
                                                <p><strong>Receive No:</strong> {selectedReceive.receive_no}</p>
                                                <p><strong>Date:</strong> {formatDate(selectedReceive.receive_date)}</p>
                                                <p><strong>Dispatch No:</strong> {selectedReceive.dispatch_no}</p>
                                                <p><strong>From:</strong> {selectedReceive.from_location}</p>
                                                <p><strong>Received At:</strong> {manufacturingUnit.name}</p>
                                                <p><strong>Status:</strong> {getStatusBadge(selectedReceive.status)}</p>
                                                {selectedReceive.received_by && (
                                                    <p><strong>Received By:</strong> {selectedReceive.received_by}</p>
                                                )}
                                                {selectedReceive.notes && (
                                                    <p><strong>Inspection Notes:</strong> {selectedReceive.notes}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">Summary</h6>
                                                <p><strong>Total Items:</strong> {selectedReceive.total_items}</p>
                                                <p><strong>Total Received:</strong> {selectedReceive.total_received}</p>
                                                <p><strong>Total Damage:</strong> {selectedReceive.total_damage}</p>
                                                <p><strong>Good Stock Added:</strong> {(selectedReceive.total_received || 0) - (selectedReceive.total_damage || 0)} units</p>
                                                <p><strong>Received Percentage:</strong> 
                                                    {selectedReceive.total_dispatched > 0 
                                                        ? ((selectedReceive.total_received / selectedReceive.total_dispatched) * 100).toFixed(1) 
                                                        : 0}%
                                                </p>
                                                {selectedReceive.created_at && (
                                                    <p><strong>Created:</strong> {formatDateTime(selectedReceive.created_at)}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <h6 className="fw-bold mb-3">Received Items at Manufacturing Unit</h6>
                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Product / Raw Material</th>
                                                <th>SKU</th>
                                                <th>Dispatched</th>
                                                <th>Received</th>
                                                <th>Damage</th>
                                                <th>Good Stock</th>
                                                <th>Reason</th>
                                                <th>Unit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedReceive.items && selectedReceive.items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td><strong>{item.product_name}</strong></td>
                                                    <td><code>{item.sku}</code></td>
                                                    <td className="text-primary">{item.dispatched_quantity}</td>
                                                    <td className="text-success fw-bold">{item.received_quantity}</td>
                                                    <td className="text-danger">{item.damage_quantity > 0 ? item.damage_quantity : '-'}</td>
                                                    <td className="text-success fw-bold">{(item.received_quantity || 0) - (item.damage_quantity || 0)}</td>
                                                    <td>{item.damage_reason || '-'}</td>
                                                    <td>{item.unit}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan="2" className="text-end fw-bold">Totals:</td>
                                                <td className="fw-bold">{selectedReceive.total_dispatched}</td>
                                                <td className="fw-bold text-success">{selectedReceive.total_received}</td>
                                                <td className="fw-bold text-danger">{selectedReceive.total_damage}</td>
                                                <td className="fw-bold text-success">{(selectedReceive.total_received || 0) - (selectedReceive.total_damage || 0)}</td>
                                                <td colSpan="2"></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                
                                {selectedReceive.total_damage > 0 && (
                                    <div className="alert alert-warning mt-3">
                                        <AlertCircle size={16} className="me-2" />
                                        <strong>Quality Alert:</strong> {selectedReceive.total_damage} units were damaged during transit. These have been moved to waste stock and are not available for production.
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RawMaterialReceive;